import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import api from '@/lib/api';

interface OrderDetail {
  id: string;
  number: string;
  channel: string;
  status: string;
  total: number;
  createdAt: string;
  customer: { name: string; email: string; phone: string };
  address: { street: string; number: string; complement: string; neighborhood: string; city: string; state: string; zipCode: string };
  items: Array<{ id: string; productName: string; sku: string; quantity: number; unitPrice: number; total: number }>;
  timeline: Array<{ id: string; status: string; createdAt: string; note: string }>;
}

const statusLabels: Record<string, { label: string; color: string }> = {
  RECEBIDO: { label: 'Recebido', color: 'bg-gray-100 text-gray-700' },
  PAGO: { label: 'Pago', color: 'bg-blue-100 text-blue-700' },
  EM_SEPARACAO: { label: 'Em Separação', color: 'bg-yellow-100 text-yellow-700' },
  SEPARADO: { label: 'Separado', color: 'bg-purple-100 text-purple-700' },
  EMBALADO: { label: 'Embalado', color: 'bg-indigo-100 text-indigo-700' },
  EXPEDIDO: { label: 'Expedido', color: 'bg-orange-100 text-orange-700' },
  EM_TRANSITO: { label: 'Em Trânsito', color: 'bg-cyan-100 text-cyan-700' },
  ENTREGUE: { label: 'Entregue', color: 'bg-green-100 text-green-700' },
  CANCELADO: { label: 'Cancelado', color: 'bg-red-100 text-red-700' },
};

const statusFlow = ['RECEBIDO', 'PAGO', 'EM_SEPARACAO', 'SEPARADO', 'EMBALADO', 'EXPEDIDO', 'EM_TRANSITO', 'ENTREGUE'];

export default function OrderDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    api.get(`/orders/${id}`).then((r) => {
      setOrder(r.data);
      setLoadingData(false);
    }).catch(() => setLoadingData(false));
  }, [id]);

  const advanceStatus = async () => {
    if (!order) return;
    await api.patch(`/orders/${order.id}/status`, { status: 'next' });
    const r = await api.get(`/orders/${id}`);
    setOrder(r.data);
  };

  const cancelOrder = async () => {
    if (!order) return;
    await api.patch(`/orders/${order.id}/status`, { status: 'CANCELADO' });
    const r = await api.get(`/orders/${id}`);
    setOrder(r.data);
  };

  if (loadingData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="text-center py-12 text-dark-500">Pedido não encontrado</div>
    );
  }

  const nextStatus = statusFlow[statusFlow.indexOf(order.status) + 1];

  return (
    <div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-dark-100">
          <h3 className="text-sm font-semibold text-dark-500 uppercase mb-4">Informações do Pedido</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-dark-500">Número</span>
              <span className="text-sm font-medium text-dark-900">{order.number}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-dark-500">Canal</span>
              <span className="text-sm font-medium text-dark-900">{order.channel}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-dark-500">Status</span>
              <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${statusLabels[order.status]?.color || 'bg-gray-100 text-gray-700'}`}>
                {statusLabels[order.status]?.label || order.status}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-dark-500">Total</span>
              <span className="text-sm font-bold text-dark-900">
                {order.total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-dark-500">Data</span>
              <span className="text-sm text-dark-900">{new Date(order.createdAt).toLocaleDateString('pt-BR')}</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-dark-100">
          <h3 className="text-sm font-semibold text-dark-500 uppercase mb-4">Cliente</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-dark-500">Nome</span>
              <span className="text-sm font-medium text-dark-900">{order.customer?.name || '-'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-dark-500">Email</span>
              <span className="text-sm text-dark-900">{order.customer?.email || '-'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-dark-500">Telefone</span>
              <span className="text-sm text-dark-900">{order.customer?.phone || '-'}</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-dark-100">
          <h3 className="text-sm font-semibold text-dark-500 uppercase mb-4">Endereço</h3>
          <div className="space-y-2 text-sm text-dark-700">
            <p>{order.address?.street || '-'}, {order.address?.number || '-'}</p>
            {order.address?.complement && <p>{order.address.complement}</p>}
            <p>{order.address?.neighborhood || '-'}</p>
            <p>{order.address?.city || '-'} - {order.address?.state || '-'}</p>
            <p>CEP: {order.address?.zipCode || '-'}</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-dark-100 overflow-hidden mb-6">
        <div className="px-6 py-4 border-b border-dark-100">
          <h3 className="text-sm font-semibold text-dark-500 uppercase">Itens do Pedido</h3>
        </div>
        <table className="w-full">
          <thead className="bg-dark-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-dark-500 uppercase">SKU</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-dark-500 uppercase">Produto</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-dark-500 uppercase">Qtd</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-dark-500 uppercase">Preço Unit.</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-dark-500 uppercase">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-dark-100">
            {order.items?.map((item) => (
              <tr key={item.id}>
                <td className="px-6 py-4 text-sm font-medium text-primary-600">{item.sku}</td>
                <td className="px-6 py-4 text-sm text-dark-900">{item.productName}</td>
                <td className="px-6 py-4 text-sm text-dark-600 text-right">{item.quantity}</td>
                <td className="px-6 py-4 text-sm text-dark-600 text-right">
                  {item.unitPrice.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </td>
                <td className="px-6 py-4 text-sm font-medium text-dark-900 text-right">
                  {item.total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-dark-100">
          <h3 className="text-sm font-semibold text-dark-500 uppercase mb-4">Timeline</h3>
          <div className="space-y-4">
            {order.timeline?.map((event) => (
              <div key={event.id} className="flex gap-3">
                <div className="w-2 h-2 rounded-full bg-primary-600 mt-2 flex-shrink-0"></div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${statusLabels[event.status]?.color || 'bg-gray-100 text-gray-700'}`}>
                      {statusLabels[event.status]?.label || event.status}
                    </span>
                    <span className="text-xs text-dark-400">
                      {new Date(event.createdAt).toLocaleDateString('pt-BR')} {new Date(event.createdAt).toLocaleTimeString('pt-BR')}
                    </span>
                  </div>
                  {event.note && <p className="text-sm text-dark-600 mt-1">{event.note}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-dark-100">
          <h3 className="text-sm font-semibold text-dark-500 uppercase mb-4">Ações</h3>
          <div className="space-y-3">
            {nextStatus && order.status !== 'CANCELADO' && (
              <button
                onClick={advanceStatus}
                className="w-full px-4 py-3 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors"
              >
                Avançar para: {statusLabels[nextStatus]?.label || nextStatus}
              </button>
            )}
            {order.status !== 'ENTREGUE' && order.status !== 'CANCELADO' && (
              <button
                onClick={cancelOrder}
                className="w-full px-4 py-3 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors"
              >
                Cancelar Pedido
              </button>
            )}
            <button
              onClick={() => navigate('/orders')}
              className="w-full px-4 py-3 border border-dark-200 text-dark-700 rounded-lg text-sm font-medium hover:bg-dark-50 transition-colors"
            >
              Voltar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
